/**
 * Turning a media row into the words, badges and ordering a person reads.
 *
 * Pure, like the fleet's equivalent: a record in, labels out. The page does the
 * fetching and the markup, so the judgements that actually matter — what counts
 * as "expiring soon", how a shared asset is described next to a store's own —
 * are testable without a browser.
 */

import type { MediaAsset } from '../../lib/media.js';
import type { UploadStage } from '../../lib/upload.js';

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

export type MediaKindFilter = 'all' | 'video' | 'image';

export function assetKind(type: string): 'video' | 'image' | 'other' {
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('image/')) return 'image';
  return 'other';
}

export function typeLabel(type: string): string {
  switch (type) {
    case 'video/mp4':
      return 'MP4 video';
    case 'image/jpeg':
      return 'JPEG image';
    case 'image/png':
      return 'PNG image';
    case 'image/webp':
      return 'WebP image';
    default:
      return type;
  }
}

/** "0:15" and "1:04:30" — the reading a person expects on a clip. */
export function formatDuration(durationMs: number | null): string {
  if (durationMs === null || !Number.isFinite(durationMs) || durationMs <= 0) return '—';

  const totalSeconds = Math.round(durationMs / 1000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  const pad = (value: number): string => String(value).padStart(2, '0');

  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

export function formatDimensions(width: number, height: number): string {
  if (width <= 0 || height <= 0) return 'Unknown';
  return `${width} × ${height}`;
}

/**
 * Whether a file is shaped for a screen that has been turned on its side. Worth
 * saying on the card: a portrait still on a landscape TV letterboxes into two
 * black bars, and that is easier to notice here than on the wall.
 */
export function orientationLabel(width: number, height: number): string | null {
  if (width <= 0 || height <= 0) return null;
  if (width === height) return 'Square';
  return height > width ? 'Portrait' : null;
}

export function scopeLabel(storeId: string | null, storeNames: ReadonlyMap<string, string>): string {
  if (storeId === null) return 'Shared library';
  return storeNames.get(storeId) ?? storeId;
}

// ---------------------------------------------------------------------------
// Expiry
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;
/** Inside this window an expiry is worth a badge rather than a line of detail. */
export const EXPIRY_WARNING_DAYS = 7;

export interface ExpiryStatus {
  kind: 'none' | 'expired' | 'soon' | 'set';
  tone: Tone;
  label: string;
  detail: string;
}

/**
 * Expiry is enforced on the device, offline, against the asset's own field — so
 * a lapsed asset is genuinely off the wall already, not merely flagged here.
 * That is why the expired wording is past tense.
 */
export function expiryStatus(contentExpiresAt: string | null, nowMs: number): ExpiryStatus {
  if (contentExpiresAt === null || contentExpiresAt === '') {
    return { kind: 'none', tone: 'neutral', label: 'No expiry', detail: 'Plays until it is removed.' };
  }

  const at = Date.parse(contentExpiresAt);
  if (Number.isNaN(at)) {
    return {
      kind: 'none',
      tone: 'neutral',
      label: 'No expiry',
      detail: 'The stored expiry date could not be read.',
    };
  }

  const when = new Date(at).toLocaleDateString();
  if (at <= nowMs) {
    return {
      kind: 'expired',
      tone: 'danger',
      label: 'Expired',
      detail: `Stopped playing on ${when}. Screens drop it themselves, with or without a network.`,
    };
  }

  const days = Math.ceil((at - nowMs) / DAY_MS);
  if (days <= EXPIRY_WARNING_DAYS) {
    return {
      kind: 'soon',
      tone: 'warning',
      label: days === 1 ? 'Expires tomorrow' : `Expires in ${days} days`,
      detail: `Comes off the screens on ${when}.`,
    };
  }

  return { kind: 'set', tone: 'neutral', label: `Until ${when}`, detail: `Comes off the screens on ${when}.` };
}

/**
 * The stored instant as a `<input type="date">` value.
 *
 * A date is what someone actually knows — "the promo runs to the end of the
 * month" — so the field takes a date and the instant is pinned to the last
 * moment of it. The conversion is local rather than UTC on purpose: every store
 * in this chain keeps one clock, and a New Zealand evening is the next day in
 * UTC, so a UTC reading would take a promo down twelve hours early.
 */
export function toExpiryInputValue(contentExpiresAt: string | null): string {
  if (contentExpiresAt === null || contentExpiresAt === '') return '';
  const at = Date.parse(contentExpiresAt);
  if (Number.isNaN(at)) return '';

  const local = new Date(at);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}`;
}

/** The inverse: the last millisecond of that local day, as an ISO instant. */
export function fromExpiryInputValue(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (match === null) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const end = new Date(year, month - 1, day, 23, 59, 59, 999);
  if (Number.isNaN(end.getTime())) return null;
  return end.toISOString();
}

// ---------------------------------------------------------------------------
// Brands
//
// Only shared assets carry them: a brand list is how one library serves both
// Pizza Bros and Rosetown Kebab without either seeing the other's promos. An
// empty list means every brand, which is why the field says so rather than
// leaving a blank box to interpret.
// ---------------------------------------------------------------------------

export function parseBrands(value: string): string[] {
  const seen = new Set<string>();
  for (const raw of value.split(',')) {
    const brand = raw.trim().slice(0, 40);
    if (brand !== '') seen.add(brand);
  }
  return [...seen];
}

export function formatBrands(brands: readonly string[]): string {
  return brands.join(', ');
}

// ---------------------------------------------------------------------------
// Filtering and ordering
// ---------------------------------------------------------------------------

export interface MediaFilter {
  kind: MediaKindFilter;
  /** Matched against the title and the brand tags, case-insensitively. */
  query: string;
}

export function matchesFilter(asset: MediaAsset, filter: MediaFilter): boolean {
  if (filter.kind !== 'all' && assetKind(asset.type) !== filter.kind) return false;

  const needle = filter.query.trim().toLowerCase();
  if (needle === '') return true;

  if (asset.title.toLowerCase().includes(needle)) return true;
  return asset.brands.some((brand) => brand.toLowerCase().includes(needle));
}

/** Newest first, because the thing someone just uploaded is the thing they want. */
export function sortMedia(assets: readonly MediaAsset[]): MediaAsset[] {
  return [...assets].sort((left, right) => {
    const byDate = right.createdAt.localeCompare(left.createdAt);
    return byDate !== 0 ? byDate : left.title.localeCompare(right.title);
  });
}

export function filterMedia(assets: readonly MediaAsset[], filter: MediaFilter): MediaAsset[] {
  return sortMedia(assets.filter((asset) => matchesFilter(asset, filter)));
}

// ---------------------------------------------------------------------------
// Upload progress
// ---------------------------------------------------------------------------

export function describeUploadStage(stage: UploadStage): string {
  switch (stage) {
    case 'starting':
      return 'Reserving space';
    case 'thumbnail':
      return 'Making a thumbnail';
    case 'uploading':
      return 'Uploading';
    case 'finishing':
      return 'Finishing up';
    default:
      return 'Done';
  }
}

/**
 * The percentage on the bar. Only the byte transfer moves it; the stages either
 * side are quick and would otherwise make the bar jump to 5% and stall.
 */
export function progressPercent(uploadedBytes: number, totalBytes: number): number {
  if (totalBytes <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)));
}
